import React from 'react';
import { Link } from 'react-router-dom';
import { Truck, ShieldCheck, Wrench, Headphones } from 'lucide-react';
import Reveal from '../components/Reveal';
import Marquee from '../components/Marquee';

export default function About() {
  return (
    <div className="bg-cream">
      <section className="bg-forest noise relative overflow-hidden">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20 lg:py-28 grid lg:grid-cols-2 gap-12 items-center">
          <div>
            <p className="font-mono text-[11px] uppercase tracking-widest2 text-flame mb-4">About TractorParts USA</p>
            <h1 className="font-display font-extrabold uppercase tracking-tight text-cream text-4xl sm:text-5xl lg:text-6xl leading-[0.98]">
              Built by people<br />who fix things.
            </h1>
            <p className="text-cream/60 leading-relaxed mt-6 max-w-md">
              TractorParts USA exists for one reason: a tractor sitting still during planting or harvest
              costs more than any part ever will. We stock dependable components for the machines America
              farms with — and we ship them fast, anywhere in the United States.
            </p>
            <p className="text-cream/60 leading-relaxed mt-4 max-w-md">
              Every part we list is checked against factory specification, backed by a 1-year warranty,
              and supported by a parts desk that answers around the clock.
            </p>
          </div>
          <Reveal delay={0.15}>
            <div className="rounded-3xl overflow-hidden border border-white/10">
              <img
                src="https://images.unsplash.com/photo-1610259998914-d1b9afe0dc55?crop=entropy&cs=srgb&fm=jpg&q=85&w=1200"
                alt="Mechanic working on tractor engine"
                className="w-full h-72 sm:h-96 object-cover"
              />
            </div>
          </Reveal>
        </div>
      </section>

      <Marquee />

      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20">
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
          {[
            { icon: Truck, t: 'Fast, Configurable Shipping', d: 'Free shipping on orders over $99, with expedited and freight options for heavy components.' },
            { icon: ShieldCheck, t: '1-Year Warranty', d: 'Every part is covered by a full replacement warranty — no fine print, no runaround.' },
            { icon: Wrench, t: 'Fitment You Can Trust', d: 'Parts are cross-referenced to OEM numbers and verified against tractor models.' },
            { icon: Headphones, t: '24/7 Parts Desk', d: 'Call 1-800-TRACTOR any hour and talk to a specialist who knows these machines.' },
          ].map((v, i) => (
            <Reveal key={v.t} delay={i * 0.08} className="bg-white border border-emerald-950/10 rounded-3xl p-7">
              <v.icon className="w-7 h-7 text-flame" />
              <h3 className="font-display font-bold uppercase text-forest text-xl mt-4">{v.t}</h3>
              <p className="text-sm text-sub mt-2 leading-relaxed">{v.d}</p>
            </Reveal>
          ))}
        </div>
      </section>

      <section className="bg-forest noise relative">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20 grid lg:grid-cols-2 gap-12 items-center">
          <Reveal className="order-2 lg:order-1">
            <div className="rounded-3xl overflow-hidden border border-white/10">
              <img
                src="https://images.unsplash.com/photo-1566930665082-4ae9dbbb5b6b?crop=entropy&cs=srgb&fm=jpg&q=85&w=1200"
                alt="Parts warehouse and assembly"
                loading="lazy"
                className="w-full h-72 sm:h-96 object-cover"
              />
            </div>
          </Reveal>
          <Reveal className="order-1 lg:order-2">
            <p className="font-mono text-[11px] uppercase tracking-widest2 text-flame mb-3">Quality assurance</p>
            <h2 className="font-display font-extrabold uppercase tracking-tight text-cream text-4xl sm:text-5xl leading-none">
              Checked twice.<br />Shipped once.
            </h2>
            <p className="text-cream/60 leading-relaxed mt-5 max-w-md">
              Filters are burst-tested, hydraulic components are pressure-verified, electrical parts are
              bench-run before they ever reach a shelf. If a part does not meet spec, it does not ship —
              simple as that.
            </p>
            <div className="flex flex-wrap gap-3 mt-8">
              <Link to="/shop" data-testid="about-shop-button" className="bg-flame hover:bg-ember text-white font-display font-semibold uppercase tracking-wide px-7 py-3.5 rounded-full transition-colors">
                Browse the Catalog
              </Link>
              <Link to="/contact" data-testid="about-contact-button" className="border-2 border-white/20 hover:border-flame text-cream hover:text-flame font-display font-semibold uppercase tracking-wide px-7 py-3.5 rounded-full transition-colors">
                Talk to a Specialist
              </Link>
            </div>
          </Reveal>
        </div>
      </section>
    </div>
  );
}
